/**
 * The emoji offered by the in-app picker.
 *
 * Bundled rather than pulled from a package: the picker has to work on web
 * under our CSP (no external fetches) and in a native bundle, and an emoji
 * dataset dependency would add hundreds of kilobytes of names and shortcodes we
 * do not use. This is a curated set — the emoji people actually react with —
 * with enough keywords to make search useful.
 *
 * Web needs this because a browser CANNOT open the OS emoji picker: ⌃⌘Space and
 * Win+. are OS shortcuts, and no API triggers them. Native does not use this
 * list at all — it raises the real system keyboard instead.
 *
 * Entries are tuples to keep the file small and diffable:
 *   [character, name, space-separated search keywords]
 */

export type EmojiEntry = {
  char: string;
  /** Display/accessibility label, original casing ("Ireland", not "ireland"). */
  name: string;
  keywords: string;
  /** Name + keywords, lower-cased once so search does not case-fold per query. */
  search: string;
};

export type EmojiGroup = {
  id: string;
  /** Ionicons name for the category tab. */
  icon: string;
  /** i18n key for the accessible label of the tab. */
  labelKey: string;
  entries: EmojiEntry[];
};

type RawEntry = readonly [char: string, name: string, keywords: string];

function group(
  id: string,
  icon: string,
  labelKey: string,
  raw: readonly RawEntry[],
): EmojiGroup {
  return {
    id,
    icon,
    labelKey,
    entries: raw.map(([char, name, keywords]) => ({
      char,
      name,
      keywords,
      search: `${name} ${keywords}`.toLowerCase(),
    })),
  };
}

const SMILEYS: readonly RawEntry[] = [
  ["😀", "grinning", "smile happy joy grin"],
  ["😃", "grinning big eyes", "smile happy joy"],
  ["😄", "grinning smiling eyes", "smile happy joy laugh"],
  ["😁", "beaming", "smile happy grin"],
  ["😆", "grinning squinting", "laugh happy haha"],
  ["😅", "grinning sweat", "laugh nervous relief"],
  ["🤣", "rolling on the floor", "laugh lol rofl funny"],
  ["😂", "tears of joy", "laugh cry funny lol"],
  ["🙂", "slight smile", "smile"],
  ["🙃", "upside down", "silly sarcasm irony"],
  ["😉", "wink", "flirt joke"],
  ["😊", "smiling blush", "happy warm kind"],
  ["😇", "halo", "angel innocent"],
  ["🥰", "smiling hearts", "love adore affection"],
  ["😍", "heart eyes", "love crush adore"],
  ["🤩", "star struck", "wow amazed excited"],
  ["😘", "blowing a kiss", "love kiss flirt"],
  ["😗", "kissing", "kiss"],
  ["😚", "kissing closed eyes", "kiss"],
  ["😙", "kissing smiling eyes", "kiss"],
  ["🥲", "smiling tear", "grateful proud bittersweet"],
  ["😋", "savouring", "yum tasty delicious"],
  ["😛", "tongue out", "silly playful"],
  ["😜", "winking tongue", "silly joke playful"],
  ["🤪", "zany", "silly crazy goofy"],
  ["😝", "squinting tongue", "silly playful"],
  ["🤑", "money mouth", "rich money greedy"],
  ["🤗", "hugging", "hug warm welcome"],
  ["🤭", "hand over mouth", "oops giggle secret"],
  ["🤫", "shushing", "quiet secret shh"],
  ["🤔", "thinking", "hmm consider wonder"],
  ["🤐", "zipper mouth", "quiet secret"],
  ["🤨", "raised eyebrow", "sceptical suspicious doubt"],
  ["😐", "neutral", "meh blank"],
  ["😑", "expressionless", "blank meh"],
  ["😶", "no mouth", "speechless blank"],
  ["😏", "smirk", "smug sly"],
  ["😒", "unamused", "meh annoyed"],
  ["🙄", "rolling eyes", "annoyed whatever"],
  ["😬", "grimacing", "awkward eek"],
  ["😮‍💨", "exhaling", "relief sigh phew"],
  ["🤥", "lying", "liar pinocchio"],
  ["😌", "relieved", "calm content"],
  ["😔", "pensive", "sad down"],
  ["😪", "sleepy", "tired"],
  ["🤤", "drooling", "hungry want"],
  ["😴", "sleeping", "zzz tired"],
  ["😷", "medical mask", "sick ill"],
  ["🤒", "thermometer", "sick fever ill"],
  ["🤕", "bandage", "hurt injured"],
  ["🤢", "nauseated", "sick gross"],
  ["🤮", "vomiting", "sick gross"],
  ["🤧", "sneezing", "sick cold"],
  ["🥵", "hot face", "heat sweating"],
  ["🥶", "cold face", "freezing"],
  ["😵", "dizzy", "confused knocked out"],
  ["🤯", "mind blown", "shocked wow amazed"],
  ["🤠", "cowboy", "hat yeehaw"],
  ["🥳", "partying", "celebrate birthday party"],
  ["😎", "sunglasses", "cool"],
  ["🤓", "nerd", "geek glasses"],
  ["🧐", "monocle", "inspect curious"],
  ["😕", "confused", "unsure"],
  ["😟", "worried", "concerned"],
  ["🙁", "slight frown", "sad"],
  ["😮", "open mouth", "surprised wow"],
  ["😯", "hushed", "surprised"],
  ["😲", "astonished", "shocked wow"],
  ["😳", "flushed", "embarrassed blush"],
  ["🥺", "pleading", "please puppy eyes beg"],
  ["😦", "frowning open mouth", "sad"],
  ["😧", "anguished", "shocked"],
  ["😨", "fearful", "scared"],
  ["😰", "anxious sweat", "nervous worried"],
  ["😥", "sad relieved", "disappointed"],
  ["😢", "crying", "sad tear"],
  ["😭", "sobbing", "cry sad bawling"],
  ["😱", "screaming", "shocked scared"],
  ["😖", "confounded", "frustrated"],
  ["😣", "persevering", "struggle"],
  ["😞", "disappointed", "sad"],
  ["😓", "downcast sweat", "tired sad"],
  ["😩", "weary", "tired done"],
  ["😫", "tired", "exhausted"],
  ["🥱", "yawning", "bored tired"],
  ["😤", "steam from nose", "frustrated proud determined"],
  ["😡", "pouting", "angry mad rage"],
  ["😠", "angry", "mad"],
  ["🤬", "cursing", "swearing angry"],
  ["😈", "smiling horns", "devil mischief"],
  ["💀", "skull", "dead dying"],
  ["💩", "poo", "crap"],
  ["🤡", "clown", "silly"],
  ["👻", "ghost", "spooky halloween"],
  ["👽", "alien", "ufo space"],
  ["🤖", "robot", "bot"],
];

const PEOPLE: readonly RawEntry[] = [
  ["👍", "thumbs up", "yes like approve good"],
  ["👎", "thumbs down", "no dislike bad"],
  ["👌", "ok hand", "perfect fine"],
  ["🤌", "pinched fingers", "italian gesture"],
  ["✌️", "victory", "peace two"],
  ["🤞", "crossed fingers", "luck hope"],
  ["🤟", "love you gesture", "ily"],
  ["🤘", "horns", "rock metal"],
  ["🤙", "call me", "shaka hang loose"],
  ["👈", "pointing left", "direction"],
  ["👉", "pointing right", "direction"],
  ["👆", "pointing up", "direction"],
  ["👇", "pointing down", "direction"],
  ["☝️", "index up", "one point"],
  ["✋", "raised hand", "stop high five"],
  ["🖐️", "hand fingers splayed", "stop"],
  ["🖖", "vulcan salute", "spock"],
  ["👋", "waving", "hello goodbye hi bye"],
  ["🤝", "handshake", "deal agree meet"],
  ["🙏", "folded hands", "please thanks pray"],
  ["✍️", "writing hand", "write note"],
  ["💪", "flexed biceps", "strong muscle"],
  ["👏", "clapping", "applause bravo well done"],
  ["🙌", "raising hands", "celebrate praise yay"],
  ["👐", "open hands", "hug"],
  ["🤲", "palms up", "please offer"],
  ["🫶", "heart hands", "love"],
  ["🤦", "facepalm", "disbelief oops"],
  ["🤷", "shrug", "dunno idk whatever"],
  ["💅", "nail polish", "manicure sass"],
  ["👀", "eyes", "look watch see"],
  ["🧠", "brain", "smart think"],
  ["🫂", "people hugging", "hug support"],
  ["👶", "baby", "infant"],
  ["🧒", "child", "kid"],
  ["🧑", "person", "adult"],
  ["👩", "woman", "adult"],
  ["👨", "man", "adult"],
  ["🧓", "older person", "elder"],
  ["🕺", "dancing man", "dance party"],
  ["💃", "dancing woman", "dance party"],
  ["🏃", "running", "run exercise"],
  ["🚶", "walking", "walk"],
  ["🧘", "lotus position", "yoga meditate calm"],
  ["🛌", "in bed", "sleep rest"],
];

const NATURE: readonly RawEntry[] = [
  ["🐶", "dog", "puppy pet"],
  ["🐱", "cat", "kitten pet"],
  ["🐭", "mouse", "animal"],
  ["🐹", "hamster", "pet"],
  ["🐰", "rabbit", "bunny"],
  ["🦊", "fox", "animal"],
  ["🐻", "bear", "animal"],
  ["🐼", "panda", "animal"],
  ["🐨", "koala", "animal"],
  ["🐯", "tiger", "animal"],
  ["🦁", "lion", "animal"],
  ["🐮", "cow", "animal"],
  ["🐷", "pig", "animal"],
  ["🐸", "frog", "animal"],
  ["🐵", "monkey", "animal"],
  ["🙈", "see no evil", "monkey hide"],
  ["🙉", "hear no evil", "monkey"],
  ["🙊", "speak no evil", "monkey quiet"],
  ["🐔", "chicken", "animal"],
  ["🐧", "penguin", "animal"],
  ["🐦", "bird", "animal"],
  ["🦆", "duck", "animal"],
  ["🦉", "owl", "animal wise"],
  ["🐝", "bee", "insect honey"],
  ["🦋", "butterfly", "insect"],
  ["🐢", "turtle", "slow animal"],
  ["🐍", "snake", "animal"],
  ["🐙", "octopus", "animal sea"],
  ["🐠", "tropical fish", "sea"],
  ["🐬", "dolphin", "sea"],
  ["🐳", "whale", "sea"],
  ["🦄", "unicorn", "magic"],
  ["🐴", "horse", "animal"],
  ["🌵", "cactus", "plant"],
  ["🌲", "evergreen tree", "forest"],
  ["🌳", "tree", "nature"],
  ["🌴", "palm tree", "beach holiday"],
  ["🌱", "seedling", "grow plant new"],
  ["🌿", "herb", "plant leaf"],
  ["🍀", "four leaf clover", "luck"],
  ["🍁", "maple leaf", "autumn fall"],
  ["🌸", "cherry blossom", "flower spring"],
  ["🌺", "hibiscus", "flower"],
  ["🌻", "sunflower", "flower"],
  ["🌹", "rose", "flower love"],
  ["🌷", "tulip", "flower"],
  ["💐", "bouquet", "flowers gift"],
  ["🌞", "sun with face", "sunny happy"],
  ["🌝", "full moon face", "night"],
  ["🌙", "crescent moon", "night"],
  ["⭐", "star", "favourite"],
  ["🌟", "glowing star", "sparkle special"],
  ["✨", "sparkles", "shiny magic new"],
  ["⚡", "high voltage", "lightning fast"],
  ["🔥", "fire", "hot lit flame"],
  ["🌈", "rainbow", "pride colour"],
  ["☀️", "sun", "sunny weather"],
  ["⛅", "sun behind cloud", "weather"],
  ["🌧️", "rain cloud", "weather wet"],
  ["❄️", "snowflake", "cold winter"],
  ["🌊", "wave", "sea ocean water"],
];

const FOOD: readonly RawEntry[] = [
  ["🍏", "green apple", "fruit"],
  ["🍎", "red apple", "fruit"],
  ["🍐", "pear", "fruit"],
  ["🍊", "orange", "fruit"],
  ["🍋", "lemon", "fruit sour"],
  ["🍌", "banana", "fruit"],
  ["🍉", "watermelon", "fruit summer"],
  ["🍇", "grapes", "fruit"],
  ["🍓", "strawberry", "fruit"],
  ["🫐", "blueberries", "fruit"],
  ["🍒", "cherries", "fruit"],
  ["🍑", "peach", "fruit"],
  ["🥭", "mango", "fruit"],
  ["🍍", "pineapple", "fruit"],
  ["🥥", "coconut", "fruit"],
  ["🥑", "avocado", "food"],
  ["🍅", "tomato", "food"],
  ["🥕", "carrot", "vegetable"],
  ["🌽", "corn", "vegetable"],
  ["🥦", "broccoli", "vegetable"],
  ["🍞", "bread", "food"],
  ["🥐", "croissant", "pastry breakfast"],
  ["🧀", "cheese", "food"],
  ["🥚", "egg", "food breakfast"],
  ["🥓", "bacon", "breakfast"],
  ["🍔", "hamburger", "burger food"],
  ["🍟", "fries", "chips food"],
  ["🍕", "pizza", "food"],
  ["🌮", "taco", "food mexican"],
  ["🌯", "burrito", "food"],
  ["🥗", "salad", "healthy food"],
  ["🍝", "spaghetti", "pasta italian"],
  ["🍜", "ramen", "noodles"],
  ["🍣", "sushi", "japanese"],
  ["🍤", "prawn", "seafood"],
  ["🍚", "rice", "food"],
  ["🍲", "stew", "food"],
  ["🥪", "sandwich", "food lunch"],
  ["🍿", "popcorn", "cinema snack"],
  ["🍩", "doughnut", "sweet"],
  ["🍪", "cookie", "biscuit sweet"],
  ["🎂", "birthday cake", "celebrate"],
  ["🍰", "cake slice", "dessert"],
  ["🧁", "cupcake", "dessert"],
  ["🍫", "chocolate", "sweet"],
  ["🍬", "sweet", "candy"],
  ["🍦", "ice cream", "dessert"],
  ["☕", "coffee", "drink hot tea"],
  ["🍵", "tea", "drink hot"],
  ["🧃", "juice box", "drink"],
  ["🥤", "cup with straw", "drink soda"],
  ["🍺", "beer", "drink pub"],
  ["🍻", "clinking beers", "cheers drink"],
  ["🥂", "clinking glasses", "cheers celebrate"],
  ["🍷", "wine", "drink"],
  ["🍸", "cocktail", "drink"],
  ["🥃", "whisky", "drink"],
  ["🍾", "champagne", "celebrate"],
];

const ACTIVITY: readonly RawEntry[] = [
  ["⚽", "football", "soccer sport"],
  ["🏀", "basketball", "sport"],
  ["🏈", "american football", "sport"],
  ["⚾", "baseball", "sport"],
  ["🎾", "tennis", "sport"],
  ["🏐", "volleyball", "sport"],
  ["🏉", "rugby", "sport"],
  ["🎱", "pool", "billiards eight ball"],
  ["🏓", "table tennis", "ping pong sport"],
  ["🏸", "badminton", "sport"],
  ["🥊", "boxing glove", "sport fight"],
  ["⛳", "golf", "sport"],
  ["🎿", "skis", "winter sport"],
  ["🏂", "snowboarder", "winter sport"],
  ["🏋️", "weight lifting", "gym strong"],
  ["🚴", "cycling", "bike sport"],
  ["🏊", "swimming", "sport pool"],
  ["🧗", "climbing", "sport"],
  ["🎯", "bullseye", "target aim goal"],
  ["🎲", "dice", "game luck"],
  ["🎮", "video game", "gaming controller"],
  ["🕹️", "joystick", "gaming retro"],
  ["🎰", "slot machine", "gamble"],
  ["🎨", "art palette", "paint creative"],
  ["🎭", "performing arts", "theatre drama"],
  ["🎬", "clapper board", "film movie"],
  ["🎤", "microphone", "sing karaoke"],
  ["🎧", "headphones", "music listen"],
  ["🎵", "music note", "song"],
  ["🎶", "music notes", "song melody"],
  ["🎸", "guitar", "music"],
  ["🎹", "piano", "music keyboard"],
  ["🥁", "drum", "music"],
  ["🎺", "trumpet", "music"],
  ["🎻", "violin", "music"],
  ["🎉", "party popper", "celebrate congrats yay"],
  ["🎊", "confetti ball", "celebrate party"],
  ["🎈", "balloon", "party birthday"],
  ["🎁", "gift", "present birthday"],
  ["🏆", "trophy", "win award champion"],
  ["🥇", "gold medal", "first win"],
  ["🥈", "silver medal", "second"],
  ["🥉", "bronze medal", "third"],
  ["🏅", "medal", "award win"],
];

const TRAVEL: readonly RawEntry[] = [
  ["🚗", "car", "drive"],
  ["🚕", "taxi", "cab"],
  ["🚙", "suv", "car"],
  ["🚌", "bus", "transport"],
  ["🚎", "trolleybus", "transport"],
  ["🏎️", "racing car", "fast"],
  ["🚓", "police car", "emergency"],
  ["🚑", "ambulance", "emergency"],
  ["🚒", "fire engine", "emergency"],
  ["🚚", "lorry", "truck delivery"],
  ["🚲", "bicycle", "bike cycle"],
  ["🛴", "scooter", "kick"],
  ["🛵", "motor scooter", "moped"],
  ["🏍️", "motorcycle", "bike"],
  ["✈️", "aeroplane", "flight travel plane"],
  ["🚀", "rocket", "launch space fast"],
  ["🛸", "flying saucer", "ufo alien"],
  ["🚁", "helicopter", "fly"],
  ["⛵", "sailing boat", "sail sea"],
  ["🚤", "speedboat", "sea"],
  ["🛳️", "passenger ship", "cruise"],
  ["🚂", "steam locomotive", "train"],
  ["🚆", "train", "rail"],
  ["🚇", "metro", "underground tube subway"],
  ["🗺️", "world map", "travel"],
  ["🧭", "compass", "direction navigate"],
  ["🏔️", "snow mountain", "peak"],
  ["🏕️", "camping", "tent outdoors"],
  ["🏖️", "beach", "holiday sand"],
  ["🏝️", "desert island", "holiday"],
  ["🏠", "house", "home"],
  ["🏡", "house with garden", "home"],
  ["🏢", "office building", "work"],
  ["🏥", "hospital", "health"],
  ["🏦", "bank", "money"],
  ["🏨", "hotel", "stay travel"],
  ["🏫", "school", "education"],
  ["⛪", "church", "religion"],
  ["🕌", "mosque", "religion"],
  ["🕍", "synagogue", "religion"],
  ["🗼", "tower", "landmark"],
  ["🗽", "statue of liberty", "landmark new york"],
  ["🎡", "ferris wheel", "fair"],
  ["🎢", "roller coaster", "theme park"],
  ["🌃", "night city", "skyline"],
  ["🌉", "bridge at night", "city"],
];

const OBJECTS: readonly RawEntry[] = [
  ["⌚", "watch", "time"],
  ["📱", "mobile phone", "phone"],
  ["💻", "laptop", "computer work"],
  ["🖥️", "desktop computer", "work"],
  ["⌨️", "keyboard", "type"],
  ["🖨️", "printer", "print"],
  ["📷", "camera", "photo"],
  ["📸", "camera flash", "photo"],
  ["📹", "video camera", "record"],
  ["🎥", "movie camera", "film"],
  ["📺", "television", "tv"],
  ["📻", "radio", "listen"],
  ["🔋", "battery", "power"],
  ["🔌", "plug", "power"],
  ["💡", "light bulb", "idea"],
  ["🔦", "torch", "flashlight"],
  ["📖", "open book", "read"],
  ["📚", "books", "read study"],
  ["📝", "memo", "note write"],
  ["✏️", "pencil", "write"],
  ["📌", "pushpin", "pin save"],
  ["📎", "paperclip", "attach"],
  ["✂️", "scissors", "cut"],
  ["🔑", "key", "unlock"],
  ["🔒", "locked", "secure private"],
  ["🔓", "unlocked", "open"],
  ["🔔", "bell", "notification"],
  ["🔕", "bell off", "mute silent"],
  ["⏰", "alarm clock", "wake time"],
  ["⏳", "hourglass", "wait time"],
  ["💰", "money bag", "cash rich"],
  ["💳", "credit card", "pay"],
  ["💎", "gem", "diamond precious"],
  ["🎓", "graduation cap", "study degree"],
  ["👑", "crown", "king queen royal"],
  ["👓", "glasses", "see"],
  ["🕶️", "sunglasses object", "cool"],
  ["👕", "t-shirt", "clothes"],
  ["👗", "dress", "clothes"],
  ["👟", "trainer", "shoe running"],
  ["🧢", "cap", "hat"],
  ["🎒", "backpack", "school bag"],
  ["☂️", "umbrella", "rain"],
  ["🧸", "teddy bear", "toy"],
  ["🛒", "trolley", "shopping cart"],
  ["🧹", "broom", "clean"],
  ["🧼", "soap", "wash clean"],
  ["💊", "pill", "medicine"],
  ["🩹", "plaster", "bandage hurt"],
  ["🔧", "spanner", "tool fix wrench"],
  ["🔨", "hammer", "tool build"],
  ["🧲", "magnet", "attract"],
];

const SYMBOLS: readonly RawEntry[] = [
  ["❤️", "red heart", "love like"],
  ["🧡", "orange heart", "love"],
  ["💛", "yellow heart", "love"],
  ["💚", "green heart", "love"],
  ["💙", "blue heart", "love"],
  ["💜", "purple heart", "love"],
  ["🖤", "black heart", "love"],
  ["🤍", "white heart", "love"],
  ["🤎", "brown heart", "love"],
  ["💔", "broken heart", "sad heartbreak"],
  ["❣️", "heart exclamation", "love"],
  ["💕", "two hearts", "love"],
  ["💞", "revolving hearts", "love"],
  ["💓", "beating heart", "love"],
  ["💗", "growing heart", "love"],
  ["💖", "sparkling heart", "love"],
  ["💘", "heart with arrow", "love cupid"],
  ["💝", "heart with ribbon", "love gift"],
  ["💯", "hundred", "perfect score agree"],
  ["💢", "anger", "mad"],
  ["💥", "collision", "boom explode"],
  ["💫", "dizzy", "star"],
  ["💦", "sweat droplets", "water"],
  ["🕳️", "hole", "empty"],
  ["✅", "check mark", "done yes correct"],
  ["☑️", "ballot check", "done tick"],
  ["✔️", "tick", "done yes"],
  ["❌", "cross mark", "no wrong"],
  ["❎", "cross button", "no"],
  ["➕", "plus", "add"],
  ["➖", "minus", "subtract"],
  ["❓", "question", "ask"],
  ["❗", "exclamation", "important"],
  ["‼️", "double exclamation", "important"],
  ["⚠️", "warning", "caution careful"],
  ["🚫", "prohibited", "no stop"],
  ["🔞", "no one under 18", "adult"],
  ["♻️", "recycling", "green"],
  ["🔄", "arrows in circle", "refresh repeat"],
  ["🔁", "repeat", "loop"],
  ["▶️", "play", "start"],
  ["⏸️", "pause", "stop"],
  ["⏹️", "stop", "end"],
  ["🔊", "loud speaker", "sound volume"],
  ["🔇", "muted", "silent"],
  ["🔍", "magnifying glass", "search find"],
  ["🆗", "ok button", "okay"],
  ["🆕", "new button", "new"],
  ["🆓", "free button", "free"],
  ["🈶", "has charge", "japanese"],
  ["#️⃣", "keycap hash", "number"],
  ["1️⃣", "keycap one", "number first"],
  ["2️⃣", "keycap two", "number"],
  ["3️⃣", "keycap three", "number"],
];

const FLAGS: readonly RawEntry[] = [
  ["🏳️", "white flag", "surrender"],
  ["🏴", "black flag", "flag"],
  ["🏁", "chequered flag", "finish race"],
  ["🚩", "triangular flag", "marker"],
  ["🏳️‍🌈", "rainbow flag", "pride lgbtq"],
  ["🏳️‍⚧️", "transgender flag", "pride trans"],
  ["🇮🇪", "Ireland", "flag irish eire"],
  ["🇬🇧", "United Kingdom", "flag uk british"],
  ["🇺🇸", "United States", "flag usa american"],
  ["🇨🇦", "Canada", "flag canadian"],
  ["🇦🇺", "Australia", "flag australian"],
  ["🇳🇿", "New Zealand", "flag kiwi"],
  ["🇫🇷", "France", "flag french"],
  ["🇩🇪", "Germany", "flag german"],
  ["🇪🇸", "Spain", "flag spanish"],
  ["🇵🇹", "Portugal", "flag portuguese"],
  ["🇮🇹", "Italy", "flag italian"],
  ["🇳🇱", "Netherlands", "flag dutch"],
  ["🇧🇪", "Belgium", "flag belgian"],
  ["🇵🇱", "Poland", "flag polish"],
  ["🇧🇷", "Brazil", "flag brazilian"],
  ["🇲🇽", "Mexico", "flag mexican"],
  ["🇯🇵", "Japan", "flag japanese"],
  ["🇰🇷", "South Korea", "flag korean"],
  ["🇨🇳", "China", "flag chinese"],
  ["🇮🇳", "India", "flag indian"],
  ["🇳🇬", "Nigeria", "flag nigerian"],
  ["🇿🇦", "South Africa", "flag"],
];

export const EMOJI_GROUPS: EmojiGroup[] = [
  group("smileys", "happy-outline", "social.chat.emojiGroupSmileys", SMILEYS),
  group("people", "hand-left-outline", "social.chat.emojiGroupPeople", PEOPLE),
  group("nature", "leaf-outline", "social.chat.emojiGroupNature", NATURE),
  group("food", "fast-food-outline", "social.chat.emojiGroupFood", FOOD),
  group("activity", "football-outline", "social.chat.emojiGroupActivity", ACTIVITY),
  group("travel", "airplane-outline", "social.chat.emojiGroupTravel", TRAVEL),
  group("objects", "bulb-outline", "social.chat.emojiGroupObjects", OBJECTS),
  group("symbols", "heart-outline", "social.chat.emojiGroupSymbols", SYMBOLS),
  group("flags", "flag-outline", "social.chat.emojiGroupFlags", FLAGS),
];

/** Flattened once, for search. */
export const ALL_EMOJI: EmojiEntry[] = EMOJI_GROUPS.flatMap(
  (group) => group.entries,
);

/**
 * Search by name and keywords. Bounded by construction — the dataset is a fixed
 * list — but the caller still renders results in a virtualised list.
 */
export function searchEmoji(query: string): EmojiEntry[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];

  // Name matches first: someone typing "hea" wants "red heart" before every
  // entry whose keywords merely mention hearts. Both sides are lower-cased —
  // "Ireland" must be findable by typing "ireland".
  const byName: EmojiEntry[] = [];
  const byKeyword: EmojiEntry[] = [];
  for (const entry of ALL_EMOJI) {
    if (entry.name.toLowerCase().includes(needle)) byName.push(entry);
    else if (entry.search.includes(needle)) byKeyword.push(entry);
  }
  return [...byName, ...byKeyword];
}
